// useRsaPaths.js — Custom hook wrapping the rsaPathsReducer with API persistence.
// On mount: loads saved paths/lanes from the backend.
// On state change (debounced): saves paths/lanes to the backend.

import { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { rsaPathsReducer, PATH_ACTIONS } from './rsaPathsReducer';
import { createInitialState, createPath, createLane, createIntersection } from './rsaPathTypes';
import { getNextSegmentColorIndex, SEGMENT_COLORS } from '../InternalComponents/rsaPathUtils';
import { API_BASE_URL } from '../../../../API/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isLocalDev() {
  return typeof window !== 'undefined' &&
    window.location.hostname.includes('localhost');
}

function pathsUrl(projectId) {
  const base = isLocalDev() ? API_BASE_URL : '';
  return `${base}/api/projects/${projectId}/rsa-paths`;
}

function getAllColorIndices(state) {
  return [
    ...state.paths.map((p) => ({ colorIndex: p.colorIndex })),
    ...state.lanes.map((l) => ({ colorIndex: l.colorIndex })),
  ];
}

function getNextLaneColorIndex(state, pathId) {
  const parentPath = state.paths.find((path) => path.id === pathId);
  const parentColorIndex = Number(parentPath?.colorIndex);
  const hasParentColor = Number.isFinite(parentColorIndex);
  const usedColorIndices = new Set(getAllColorIndices(state).map((entry) => entry.colorIndex ?? 0));

  for (let i = 0; i < SEGMENT_COLORS.length; i += 1) {
    if (hasParentColor && i === parentColorIndex) continue;
    if (!usedColorIndices.has(i)) return i;
  }

  for (let i = 0; i < SEGMENT_COLORS.length; i += 1) {
    if (hasParentColor && i === parentColorIndex) continue;
    return i;
  }

  return getNextSegmentColorIndex(getAllColorIndices(state));
}

const ROUTE_LABEL_REGEX = /^route\s*(\d+)$/i;

function getNextRouteLabel(paths = []) {
  const safePaths = Array.isArray(paths) ? paths : [];
  let highestRouteNumber = 0;

  safePaths.forEach((path) => {
    const label = String(path?.label ?? '').trim();
    const match = label.match(ROUTE_LABEL_REGEX);
    if (!match) return;
    const routeNumber = Number(match[1]);
    if (Number.isFinite(routeNumber)) {
      highestRouteNumber = Math.max(highestRouteNumber, routeNumber);
    }
  });

  const nextRouteNumber = Math.max(safePaths.length + 1, highestRouteNumber + 1, 1);
  return `Route${nextRouteNumber}`;
}

/**
 * Sanitize a path label for use as a filesystem/Drive folder suffix.
 * Replaces non-alphanumeric chars (except - and _) with _, collapses runs,
 * trims edges, and truncates to 50 chars.
 */
export function sanitizePathLabel(label) {
  let s = String(label ?? '').trim();
  s = s.replace(/[^a-zA-Z0-9_-]/g, '_');
  s = s.replace(/_+/g, '_');
  s = s.replace(/^_|_$/g, '');
  if (s.length > 50) s = s.substring(0, 50).replace(/_$/, '');
  return s || 'Route';
}

/**
 * Ensure a path label is unique (by sanitized form) among all paths.
 * If it collides, appends _2, _3, … to the original label.
 * @param {string} label - proposed label
 * @param {Array} paths - all existing paths
 * @param {string|null} excludeId - path ID to exclude (for renames)
 * @returns {string} the (possibly suffixed) label
 */
export function makeUniqueLabel(label, paths, excludeId = null) {
  const safePaths = Array.isArray(paths) ? paths : [];
  const sanitized = sanitizePathLabel(label);
  const others = safePaths
    .filter((p) => p.id !== excludeId)
    .map((p) => sanitizePathLabel(p.label));
  if (!others.includes(sanitized)) return label;
  let suffix = 2;
  while (others.includes(sanitizePathLabel(`${label}_${suffix}`))) {
    suffix++;
  }
  return `${label}_${suffix}`;
}

const DEFAULT_SPEED_MPH = 0;

function normalizeSpeedSegments(speedSegments) {
  if (!Array.isArray(speedSegments)) return [];

  return speedSegments
    .map((seg) => {
      const mileMark = Number(seg?.mileMark);
      if (!Number.isFinite(mileMark)) return null;

      const explicitSpeed = Number(seg?.speedMph);
      const legacySpeed = Number(seg?.speed);
      const speedMph = Number.isFinite(explicitSpeed)
        ? explicitSpeed
        : (Number.isFinite(legacySpeed) ? legacySpeed : DEFAULT_SPEED_MPH);

      return {
        ...seg,
        mileMark,
        speedMph,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.mileMark - b.mileMark);
}

// ─── IndexedDB cache helpers ──────────────────────────────────────────────────
// Uses IndexedDB instead of localStorage — no practical size limit,
// so even 100+ mile paths with tens of thousands of points are fine.

const IDB_NAME = 'rsa_paths_cache';
const IDB_STORE = 'projects';
const IDB_VERSION = 1;

function openCacheDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: 'projectId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveToCacheDb(projectId, paths, lanes, intersections) {
  try {
    const db = await openCacheDb();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put({ projectId, paths, lanes, intersections: intersections ?? [], _ts: Date.now() });
    db.close();
  } catch (_) { /* IndexedDB unavailable — skip silently */ }
}

async function loadFromCacheDb(projectId) {
  try {
    const db = await openCacheDb();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(projectId);
      req.onsuccess = () => {
        db.close();
        const data = req.result;
        if (!data || !Array.isArray(data.paths)) resolve(null);
        else resolve(data);
      };
      req.onerror = () => { db.close(); resolve(null); };
    });
  } catch (_) {
    return null;
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useRsaPaths — manages the full paths+lanes state for one project.
 *
 * @param {number|string|null} projectId
 * @returns {{ paths, lanes, activePathId, activeLaneId, activeEntityType,
 *             laneEditMode, selectedLaneWaypointIndices, actions }}
 */
export function useRsaPaths(projectId, options = {}) {
  // When `pauseDestructiveReloads` is true, a 409 conflict from the save
  // endpoint will only update pathsVersionRef and let the next save retry,
  // instead of bumping reloadNonce (which RESETs state and HYDRATEs from
  // possibly-stale cache, destroying any unsaved changes — e.g., waypoints
  // the user just clicked while a save was in flight). Callers should set
  // this to true while the user is in a continuous-edit mode like manual
  // lane placement.
  //
  // Accepts a boolean OR a function returning a boolean. The function form
  // is evaluated at 409 response time, which lets the caller close over
  // state from this hook's own return value (e.g. speedEditMode/laneEditMode)
  // without hitting a temporal-dead-zone reference.
  const { pauseDestructiveReloads = false } = options;
  const pauseDestructiveReloadsRef = useRef(pauseDestructiveReloads);
  useEffect(() => {
    pauseDestructiveReloadsRef.current = pauseDestructiveReloads;
  }, [pauseDestructiveReloads]);

  const [state, dispatch] = useReducer(rsaPathsReducer, undefined, createInitialState);

  // Track the last projectId we loaded so we can reset on project switch.
  // `loadedProjectIdRef` is the bare project id (used as the save-effect gate
  // and by markLocalChangeBeforeLoad). `loadedLoadKeyRef` stores
  // "<projectId>:<reloadNonce>" so the load effect can dedupe on the
  // projectId+nonce pair. Keeping the two separate matters because the save
  // effect compares the ref to plain `projectId`; if we stuffed the load key
  // in there, that compare would never match and saves would silently no-op.
  const loadedProjectIdRef = useRef(null);
  const loadedLoadKeyRef = useRef(null);
  const hasLoadedInitialStateRef = useRef(false);
  const hasLocalChangesBeforeLoadRef = useRef(false);

  // Debounce timer for save.
  const saveTimerRef = useRef(null);
  // Bumped by the `reload` action below to force the load effect to re-run
  // and re-fetch paths/lanes/intersections from the backend.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Optimistic concurrency control: the version of paths_json this client
  // is working against. Updated on every successful load + successful save.
  // Sent as `expected_version` on each POST; on 409 conflict we trigger a
  // reload so the client picks up whatever the other tab/user wrote.
  const pathsVersionRef = useRef(0);

  // ── Load from localStorage (instant) then backend when projectId changes ──

  useEffect(() => {
    if (!projectId) {
      loadedProjectIdRef.current = null;
      loadedLoadKeyRef.current = null;
      hasLoadedInitialStateRef.current = false;
      hasLocalChangesBeforeLoadRef.current = false;
      dispatch({ type: PATH_ACTIONS.RESET });
      return;
    }
    // Skip only when this exact (projectId, reloadNonce) pair has already
    // been loaded — so projectId change OR reloadNonce bump triggers a
    // re-fetch.
    const loadKey = `${projectId}:${reloadNonce}`;
    if (loadedLoadKeyRef.current === loadKey) return;
    loadedLoadKeyRef.current = loadKey;
    loadedProjectIdRef.current = projectId;
    hasLoadedInitialStateRef.current = false;
    hasLocalChangesBeforeLoadRef.current = false;

    // Reset state while loading so we don't show stale data.
    dispatch({ type: PATH_ACTIONS.RESET });

    let cancelled = false;
    const hydrateFromData = (data) => {
      if (!data || !Array.isArray(data.paths)) return false;
      dispatch({
        type: PATH_ACTIONS.HYDRATE,
        state: {
          // Drop legacy `analysisType` field — it was a default that
          // nothing read; the next debounced save will rewrite the row
          // without it. Keeps paths_json simple per the persistence
          // intent.
          paths: (data.paths ?? []).map(({ analysisType: _at, ...rest }) => rest),
          lanes: (data.lanes ?? []).map((lane) => ({
            ...lane,
            speedSegments: normalizeSpeedSegments(lane?.speedSegments),
          })),
          intersections: data.intersections ?? [],
          xmlToJsonMap: data.xmlToJsonMap ?? {},
          activePathId: data.paths?.[0]?.id ?? null,
          activeLaneId: null,
          activeIntersectionId: null,
          activeEntityType: 'path',
          laneEditMode: false,
          speedEditMode: false,
          selectedLaneWaypointIndices: [],
        },
      });
      return true;
    };

    // 1) Fast restore from IndexedDB cache
    loadFromCacheDb(projectId).then((cached) => {
      if (cancelled) return;
      if (cached && hydrateFromData(cached)) {
        hasLoadedInitialStateRef.current = true;
        // eslint-disable-next-line no-console
        console.log(
          '[useRsaPaths] LOAD from IndexedDB cache',
          'projectId=', projectId,
          'paths=', cached.paths?.length ?? 0,
          'lanes=', cached.lanes?.length ?? 0,
          'intersections=', cached.intersections?.length ?? 0,
        );
      } else {
        // eslint-disable-next-line no-console
        console.log(
          '[useRsaPaths] LOAD IndexedDB cache empty',
          'projectId=', projectId,
        );
      }
    });

    // 2) Fetch from backend (authoritative source) — overwrites cache data.
    //    MySQL rsa_project_paths is the single source of truth; we used to
    //    fall back to MongoDB pipeline_project_lanes when MySQL was empty,
    //    but that caused stale paths to resurrect after a deliberate wipe
    //    and the Mongo doc was always behind anyway (only written on Save
    //    Path / RSA submit, not on every edit).
    const backendUrl = pathsUrl(projectId);
    // eslint-disable-next-line no-console
    console.log('[useRsaPaths] LOAD → GET', backendUrl);
    fetch(backendUrl, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          let errBody = null;
          try { errBody = await r.text(); } catch (_) {}
          console.error(
            '[useRsaPaths] LOAD backend non-OK',
            'status=', r.status,
            'body=', errBody,
          );
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled || hasLocalChangesBeforeLoadRef.current) {
          if (hasLocalChangesBeforeLoadRef.current) {
            console.warn(
              '[useRsaPaths] LOAD backend response IGNORED — local changes already made before load completed',
              'projectId=', projectId,
            );
          }
          return;
        }
        // Capture the authoritative version (defaults to 0 if backend
        // returned an empty payload — first save will bump it to 1).
        if (data && typeof data.version === 'number') {
          pathsVersionRef.current = data.version;
        }
        const hasBackendPaths = data && Array.isArray(data.paths) && data.paths.length > 0;
        // eslint-disable-next-line no-console
        console.log(
          '[useRsaPaths] LOAD backend response',
          'projectId=', projectId,
          'version=', data?.version ?? '(none)',
          'paths=', data?.paths?.length ?? 0,
          'lanes=', data?.lanes?.length ?? 0,
          'intersections=', data?.intersections?.length ?? 0,
          hasBackendPaths ? '— hydrating' : '— empty, keeping cache',
        );
        if (hasBackendPaths) {
          hydrateFromData(data);
          saveToCacheDb(projectId, data.paths, data.lanes ?? [], data.intersections ?? []);
        }
      })
      .catch((err) => {
        console.error(
          '[useRsaPaths] LOAD network error — IndexedDB cache (if any) remains active',
          'projectId=', projectId,
          err,
        );
      })
      .finally(() => {
        if (!cancelled) {
          hasLoadedInitialStateRef.current = true;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, reloadNonce]);

  // ── Save to IndexedDB + backend (debounced 1 s after last change) ─────────

  useEffect(() => {
    if (!projectId) return;
    // Don't save until we have actually loaded for this project.
    if (loadedProjectIdRef.current !== projectId) return;
    if (!hasLoadedInitialStateRef.current) return;

    // Save to IndexedDB immediately (async but fast, no size limit)
    saveToCacheDb(projectId, state.paths, state.lanes, state.intersections);

    // Save to backend (debounced). Sends `expected_version` so the backend
    // can apply CAS on paths_version. On 200, update our ref to the new
    // version returned by the server. On 409 conflict, trigger a reload
    // (bumping reloadNonce re-fires the load effect, which re-fetches and
    // updates pathsVersionRef.current). Other failures are ignored as
    // before — the next debounce tick will retry.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const expectedVersion = pathsVersionRef.current;
      const url = pathsUrl(projectId);
      const counts = {
        paths: state.paths.length,
        lanes: state.lanes.length,
        intersections: state.intersections.length,
      };
      // eslint-disable-next-line no-console
      console.log(
        '[useRsaPaths] SAVE → POST',
        url,
        'expected_version=', expectedVersion,
        'counts=', counts,
      );
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paths: state.paths,
          lanes: state.lanes,
          intersections: state.intersections,
          xmlToJsonMap: state.xmlToJsonMap,
          expected_version: expectedVersion,
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            let newVersion = null;
            try {
              const body = await res.json();
              if (body && typeof body.version === 'number') {
                pathsVersionRef.current = body.version;
                newVersion = body.version;
              }
            } catch (_) { /* response not JSON; leave ref alone */ }
            // eslint-disable-next-line no-console
            console.log(
              '[useRsaPaths] SAVE ok',
              'status=', res.status,
              'new version=', newVersion ?? '(unchanged)',
              'counts=', counts,
            );
            return;
          }
          if (res.status === 409) {
            let serverVersion = null;
            try {
              const body = await res.json();
              // Adopt the server's version so the next save targets it,
              // then trigger a reload to pull the conflicting state.
              if (body && typeof body.version === 'number') {
                pathsVersionRef.current = body.version;
                serverVersion = body.version;
              }
            } catch (_) { /* ignore parse error */ }
            const pauseRaw = pauseDestructiveReloadsRef.current;
            const isPaused = typeof pauseRaw === 'function' ? !!pauseRaw() : !!pauseRaw;
            if (isPaused) {
              // User is mid-action (e.g., manual lane placement, speed
              // editing, lane-point editing). The 409 is almost always our
              // own race — two debounced saves overlap on the same
              // expected_version because the first didn't return before the
              // second fired. Local state already has the latest user input;
              // reloading would RESET+HYDRATE from (possibly stale) cache
              // and erase their work — and would also silently exit the
              // active edit mode. Adopt the new version and let the next
              // save go through.
              console.warn(
                '[useRsaPaths] save 409 during active edit — version adopted, reload deferred',
                'expected=', expectedVersion, 'server=', serverVersion,
              );
              return;
            }
            console.warn(
              '[useRsaPaths] save rejected by server (concurrent edit detected); reloading paths_json',
              'expected=', expectedVersion, 'server=', serverVersion,
            );
            setReloadNonce((n) => n + 1);
            return;
          }
          // Any other non-OK status (4xx/5xx) used to be silently dropped.
          // Surface it so we can see when writes are actually failing.
          let errBody = null;
          try { errBody = await res.text(); } catch (_) {}
          console.error(
            '[useRsaPaths] SAVE failed',
            'status=', res.status,
            'expected_version=', expectedVersion,
            'counts=', counts,
            'body=', errBody,
          );
        })
        .catch((err) => {
          // Network error / CORS / DNS — also previously swallowed.
          console.error(
            '[useRsaPaths] SAVE network error',
            'expected_version=', expectedVersion,
            'counts=', counts,
            err,
          );
        });
    }, 1000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, state.paths, state.lanes, state.intersections, state.xmlToJsonMap]);

  const markLocalChangeBeforeLoad = useCallback(() => {
    if (loadedProjectIdRef.current && !hasLoadedInitialStateRef.current) {
      hasLocalChangesBeforeLoadRef.current = true;
    }
  }, []);

  // ── Stable action creators ────────────────────────────────────────────────

  const actions = {
    // ── Paths ──

    addPath: useCallback(() => {
      markLocalChangeBeforeLoad();
      const colorIndex = getNextSegmentColorIndex(getAllColorIndices(state));
      const id = crypto.randomUUID();
      const label = makeUniqueLabel(getNextRouteLabel(state.paths), state.paths);
      const path = createPath({ id, label, colorIndex });
      dispatch({ type: PATH_ACTIONS.ADD_PATH, path });
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_PATH, id });
      return id;
    }, [state, markLocalChangeBeforeLoad]),

    addPathWithColor: useCallback((colorIndex) => {
      markLocalChangeBeforeLoad();
      const id = crypto.randomUUID();
      const label = makeUniqueLabel(getNextRouteLabel(state.paths), state.paths);
      const path = createPath({ id, label, colorIndex });
      dispatch({ type: PATH_ACTIONS.ADD_PATH, path });
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_PATH, id });
      return id;
    }, [state.paths, markLocalChangeBeforeLoad]),

    updatePath: useCallback((id, updates) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_PATH, id, updates });
    }, [markLocalChangeBeforeLoad]),

    deletePath: useCallback((id) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.DELETE_PATH, id });
    }, [markLocalChangeBeforeLoad]),

    setPathWaypoints: useCallback((id, waypoints) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_PATH, id, updates: { waypoints } });
    }, [markLocalChangeBeforeLoad]),

    setPathData: useCallback((id, pathData) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_PATH, id, updates: { pathData } });
    }, [markLocalChangeBeforeLoad]),

    setActivePath: useCallback((id) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_PATH, id });
    }, [markLocalChangeBeforeLoad]),

    // ── Lanes ──

    addLane: useCallback((pathId, overrides = {}) => {
      markLocalChangeBeforeLoad();
      const overrideColorIndex = Number(overrides?.colorIndex);
      const colorIndex = Number.isFinite(overrideColorIndex)
        ? overrideColorIndex
        : getNextLaneColorIndex(state, pathId);
      const id = crypto.randomUUID();
      const lane = createLane(pathId, { id, colorIndex, ...overrides });
      dispatch({ type: PATH_ACTIONS.ADD_LANE, lane });
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_LANE, id });
      return id;
    }, [state, markLocalChangeBeforeLoad]),

    addLaneWithColor: useCallback((pathId, colorIndex, overrides = {}) => {
      markLocalChangeBeforeLoad();
      const id = crypto.randomUUID();
      const lane = createLane(pathId, { id, colorIndex, ...overrides });
      dispatch({ type: PATH_ACTIONS.ADD_LANE, lane });
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_LANE, id });
      return id;
    }, [markLocalChangeBeforeLoad]),

    updateLane: useCallback((id, updates) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id, updates });
    }, [markLocalChangeBeforeLoad]),

    deleteLane: useCallback((id) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.DELETE_LANE, id });
    }, [markLocalChangeBeforeLoad]),

    setLaneWaypoints: useCallback((id, waypoints) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id, updates: { waypoints } });
    }, [markLocalChangeBeforeLoad]),

    setLaneData: useCallback((id, pathData) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id, updates: { pathData } });
    }, [markLocalChangeBeforeLoad]),

    setActiveLane: useCallback((id) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_LANE, id });
    }, [markLocalChangeBeforeLoad]),

    // ── Lane edit mode ──

    enterLaneEditMode: useCallback(() => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_LANE_EDIT_MODE, enabled: true });
    }, [markLocalChangeBeforeLoad]),

    exitLaneEditMode: useCallback(() => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_LANE_EDIT_MODE, enabled: false });
    }, [markLocalChangeBeforeLoad]),

    setSelectedLaneWaypoints: useCallback((indices) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_SELECTED_LANE_WAYPOINTS, indices });
    }, [markLocalChangeBeforeLoad]),

    /**
     * Move all currently selected lane waypoints by a lat/lng delta.
     * Called from useLanePointEditor after a drag-end.
     */
    moveSelectedLaneWaypoints: useCallback((laneId, deltaLat, deltaLng, fallbackIdx = null) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const selectedIndices = Array.isArray(state.selectedLaneWaypointIndices)
        ? state.selectedLaneWaypointIndices
        : [];
      // If the dragged point isn't in the selection (or nothing is selected), move just that point
      const indicesToMove = (fallbackIdx !== null && !selectedIndices.includes(fallbackIdx))
        ? [fallbackIdx]
        : selectedIndices;
      const newWaypoints = lane.waypoints.map((wp, i) =>
        indicesToMove.includes(i)
          ? { ...wp, lat: wp.lat + deltaLat, lng: wp.lng + deltaLng }
          : wp
      );
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id: laneId, updates: { waypoints: newWaypoints } });
    }, [state.lanes, state.selectedLaneWaypointIndices, markLocalChangeBeforeLoad]),

    insertLaneWaypoint: useCallback((laneId, insertIdx, lat, lng, extras) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const next = [...lane.waypoints];
      const wp = { lat, lng };
      if (extras && extras.bridged === true) wp.bridged = true;
      next.splice(insertIdx, 0, wp);
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id: laneId, updates: { waypoints: next } });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    removeLaneWaypoint: useCallback((laneId, waypointIdx) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const next = lane.waypoints.filter((_, i) => i !== waypointIdx);
      dispatch({ type: PATH_ACTIONS.UPDATE_LANE, id: laneId, updates: { waypoints: next } });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    // ── Speed edit mode ──

    enterSpeedEditMode: useCallback(() => {
      dispatch({ type: PATH_ACTIONS.SET_SPEED_EDIT_MODE, enabled: true });
    }, []),

    exitSpeedEditMode: useCallback(() => {
      dispatch({ type: PATH_ACTIONS.SET_SPEED_EDIT_MODE, enabled: false });
    }, []),

    updateLaneSpeedSegments: useCallback((laneId, speedSegments) => {
      markLocalChangeBeforeLoad();
      dispatch({
        type: PATH_ACTIONS.UPDATE_LANE,
        id: laneId,
        updates: { speedSegments: normalizeSpeedSegments(speedSegments) },
      });
    }, [markLocalChangeBeforeLoad]),

    addSpeedBreakpoint: useCallback((laneId, mileMark, speedMph = DEFAULT_SPEED_MPH) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const segs = normalizeSpeedSegments(lane.speedSegments);
      // Don't add duplicate breakpoints (within 0.005 mi tolerance)
      if (segs.some((s) => Math.abs(s.mileMark - mileMark) < 0.005)) return;
      segs.push({ mileMark, speedMph });
      dispatch({
        type: PATH_ACTIONS.UPDATE_LANE,
        id: laneId,
        updates: { speedSegments: normalizeSpeedSegments(segs) },
      });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    removeSpeedBreakpoint: useCallback((laneId, index) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const segs = normalizeSpeedSegments(lane.speedSegments);
      // Don't remove the first breakpoint (mile 0)
      if (index <= 0) return;
      segs.splice(index, 1);
      dispatch({
        type: PATH_ACTIONS.UPDATE_LANE,
        id: laneId,
        updates: { speedSegments: normalizeSpeedSegments(segs) },
      });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    updateSpeedAtBreakpoint: useCallback((laneId, index, speedMph) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const segs = normalizeSpeedSegments(lane.speedSegments);
      if (index < 0 || index >= segs.length) return;
      const nextSpeed = Number(speedMph);
      if (!Number.isFinite(nextSpeed) || nextSpeed < 0) return;
      segs[index] = { ...segs[index], speedMph: nextSpeed };
      dispatch({
        type: PATH_ACTIONS.UPDATE_LANE,
        id: laneId,
        updates: { speedSegments: normalizeSpeedSegments(segs) },
      });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    updateSpeedBreakpointMile: useCallback((laneId, index, mileMark) => {
      markLocalChangeBeforeLoad();
      const lane = state.lanes.find((l) => l.id === laneId);
      if (!lane) return;
      const segs = normalizeSpeedSegments(lane.speedSegments);
      if (index < 0 || index >= segs.length) return;
      const nextMile = Number(mileMark);
      if (!Number.isFinite(nextMile) || nextMile < 0) return;
      // First segment must stay at mile 0 — ignore attempts to move it.
      if (index === 0) return;
      // Keep segments strictly ordered (end of one = start of next).
      const prev = segs[index - 1]?.mileMark ?? 0;
      const next = index + 1 < segs.length ? segs[index + 1].mileMark : Infinity;
      const clamped = Math.min(Math.max(nextMile, prev + 0.001), next - 0.001);
      segs[index] = { ...segs[index], mileMark: clamped };
      dispatch({
        type: PATH_ACTIONS.UPDATE_LANE,
        id: laneId,
        updates: { speedSegments: normalizeSpeedSegments(segs) },
      });
    }, [state.lanes, markLocalChangeBeforeLoad]),

    // ── Intersections ──

    addIntersection: useCallback((insertionPoint) => {
      markLocalChangeBeforeLoad();
      const id = crypto.randomUUID();
      const maxIndex = state.intersections.reduce((max, i) => Math.max(max, i.maneuverIndex || 0), 0);
      const intersection = createIntersection({ id, insertionPoint, maneuverIndex: maxIndex + 1 });
      dispatch({ type: PATH_ACTIONS.ADD_INTERSECTION, intersection });
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_INTERSECTION, id });
      return id;
    }, [state.intersections, markLocalChangeBeforeLoad]),

    updateIntersection: useCallback((id, updates) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.UPDATE_INTERSECTION, id, updates });
    }, [markLocalChangeBeforeLoad]),

    deleteIntersection: useCallback((id) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.DELETE_INTERSECTION, id });
    }, [markLocalChangeBeforeLoad]),

    setActiveIntersection: useCallback((id) => {
      dispatch({ type: PATH_ACTIONS.SET_ACTIVE_INTERSECTION, id });
    }, []),

    // ── Global ──

    reset: useCallback(() => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.RESET });
    }, [markLocalChangeBeforeLoad]),

    // ── File mapping ──

    setXmlToJsonMap: useCallback((map) => {
      markLocalChangeBeforeLoad();
      dispatch({ type: PATH_ACTIONS.SET_XML_TO_JSON_MAP, map });
    }, [markLocalChangeBeforeLoad]),

    /** Force-hydrate (e.g. after a manual clear). */
    hydrate: useCallback((newState) =>
      dispatch({ type: PATH_ACTIONS.HYDRATE, state: newState }), []),

    /** Re-fetch paths/lanes/intersections from the backend. Used by the
     * Intersection panel's refresh button so users can pick up lanes
     * created elsewhere without reloading the page. */
    reload: useCallback(() => {
      hasLocalChangesBeforeLoadRef.current = false;
      setReloadNonce((n) => n + 1);
    }, []),
  };

  return { ...state, dispatch, actions, SEGMENT_COLORS, pathsVersionRef };
  // State includes: paths, lanes, activePathId, activeLaneId, activeEntityType,
  // laneEditMode, speedEditMode, selectedLaneWaypointIndices.
  // pathsVersionRef.current holds the optimistic-concurrency version of
  // paths_json on the server. Read it to send `expected_version` on writes
  // outside the auto-save loop (e.g. the path-delete endpoint); update it
  // to whatever `version` the server returns on success.
}
